import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle2, Send, ShieldCheck } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, Td, Th } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { LabeledBar, fmtDate, fmtRelative, label } from '@/features/dossier/components/primitives'
import { approveCampaign, getCampaign, materialise, stopCampaign,
         type CampaignDetail } from './api'
import { Cost, Parts, Reach, StatusChip } from './components'
import { DateTimePicker } from '@/components/erp/date-picker'

/**
 * One campaign: what it says, who it reaches, who approved it, and whether it arrived.
 *
 * ## The approval panel shows facts, not estimates
 *
 * It appears only after materialisation, because that is what makes the three numbers on it
 * real: the exact reach, the exact number of people who could not be contacted, and the cost.
 * An approval given against "roughly forty thousand people" is not an approval of anything in
 * particular, and this screen refuses to offer one.
 *
 * ## Delivery is reported as two numbers
 *
 * Accepted and delivered, never summed. The gap between them is the set of people the campaign
 * believes it reached and did not — on a polling-day morning that set is the whole point of
 * looking at this screen.
 */
export function CampaignPage() {
  const { id = '' } = useParams()
  const authorities = useSession((s) => s.authorities)
  const canCompose = hasPermission(authorities, 'comms:compose')
  const canApprove = hasPermission(authorities, 'comms:approve')
  const canStop = hasPermission(authorities, 'comms:stop')

  const [data, setData] = useState<CampaignDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await getCampaign(id))
    } catch (err) {
      setError(explain(err))
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  async function run(work: () => Promise<unknown>, message?: string) {
    setBusy(true); setError(null); setNotice(null)
    try {
      await work()
      if (message) setNotice(message)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  if (error && !data) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Opening the campaign" />

  const { campaign, audiences, template, breakdown, sample } = data
  const accepted = (breakdown.ACCEPTED ?? 0) + (breakdown.DELIVERED ?? 0)
  const delivered = breakdown.DELIVERED ?? 0
  const failed = (breakdown.FAILED ?? 0) + (breakdown.UNDELIVERED ?? 0)
  const isDraft = campaign.statusCode === 'DRAFT'
  const inFlight = campaign.statusCode === 'SENDING' || campaign.statusCode === 'SCHEDULED'

  return (
    <div className="space-y-3">
      {error && <ErrorStrip message={error} />}
      {notice && (
        <p className="rounded border border-emerald-500/30 bg-emerald-500/5 px-3 py-2
                      text-[11px] text-emerald-700 dark:text-emerald-400">{notice}</p>
      )}

      <Panel>
        <div className="space-y-1.5 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-48 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[13px] font-medium">{campaign.displayName}</span>
                <StatusChip code={campaign.statusCode} />
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {campaign.ref} · {campaign.channelName ?? 'no channel'} ·{' '}
                {campaign.templateName ?? 'no template'}
              </p>
              <p className="mt-1 text-[11px]">{campaign.purpose}</p>
            </div>
            {canStop && inFlight && (
              <button type="button" disabled={busy}
                      onClick={() => {
                        const reason = window.prompt(
                          'Why is this send being stopped? (10 characters minimum — it goes on '
                          + 'the record beside what already went out)')
                        if (reason) void run(() => stopCampaign(id, reason), 'Stopped.')
                      }}
                      className="rounded border border-rose-500/40 px-2 py-1 text-[11px]
                                 text-rose-700 dark:text-rose-400">
                Stop this send
              </button>
            )}
          </div>

          {campaign.approvedBy ? (
            <p className="inline-flex items-center gap-1.5 rounded border border-emerald-500/30
                          bg-emerald-500/5 px-2 py-1 text-[11px] text-emerald-700
                          dark:text-emerald-400">
              <ShieldCheck className="size-3.5" />
              Approved by {campaign.approvedBy.slice(0, 8)} on {fmtDate(campaign.approvedAt)}
              {campaign.scheduledAt && ` · sending from ${fmtDate(campaign.scheduledAt)}`}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Not approved. Nothing has been sent and nothing will be until somebody puts their
              name on it.
            </p>
          )}

          {campaign.stoppedReason && (
            <p className="rounded border border-border px-2 py-1 text-[11px]
                          text-muted-foreground">
              Stopped {fmtRelative(campaign.stoppedAt)} — {campaign.stoppedReason}. What had
              already gone out stays on the record.
            </p>
          )}
        </div>
      </Panel>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="space-y-3">
          <Panel>
            <div className="space-y-1.5 p-3">
              <p className="text-[11px] font-medium">The message</p>
              {template ? (
                <>
                  {template.subject && (
                    <p className="text-[12px] font-medium">{template.subject}</p>
                  )}
                  <p className="whitespace-pre-wrap rounded border border-border bg-muted/20 p-2
                                text-[12px]">{template.body}</p>
                  {template.footer && (
                    <p className="text-[10px] text-muted-foreground">
                      Every message ends with: “{template.footer}”
                    </p>
                  )}
                  <Parts parts={template.estimatedParts} characters={template.body.length} />
                  {template.placeholders.length > 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      Fills in {template.placeholders.join(', ')} per person. A recipient with
                      no value for one is counted as unreachable rather than sent a message
                      with the placeholder still in it.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  The template this campaign used has been removed. What was SENT is unaffected
                  — each recipient row holds its own frozen copy.
                </p>
              )}
            </div>
          </Panel>

          <Panel>
            <div className="p-2">
              <p className="px-1 text-[11px] font-medium">
                A sample of the recipients ({sample.length} of{' '}
                {campaign.recipientCount + campaign.suppressedCount})
              </p>
              <p className="px-1 text-[10px] text-muted-foreground">
                A sample, never the list, and never with an address on it.
              </p>
              <ErpTable head={<>
                <Th>Person</Th>
                <Th>Status</Th>
                <Th className="axis-hide-sm">Sent</Th>
                <Th className="axis-hide-sm">Delivered</Th>
                <Th>Why not</Th>
              </>}>
                {sample.length === 0 && (
                  <EmptyRow colSpan={5}
                            message="Nothing materialised yet. Resolve the audience first." />
                )}
                {sample.map((r) => (
                  <tr key={r.id}>
                    <Td>
                      <span className="text-[11px]">
                        {r.personName ?? (r.voterId ? 'a voter' : 'a mobilizer')}
                      </span>
                    </Td>
                    <Td><StatusChip code={r.statusCode} /></Td>
                    <Td className="axis-hide-sm">
                      <span className="text-[10px] text-muted-foreground">
                        {r.sentAt ? fmtRelative(r.sentAt) : '—'}
                      </span>
                    </Td>
                    <Td className="axis-hide-sm">
                      <span className="text-[10px] text-muted-foreground">
                        {r.deliveredAt ? fmtRelative(r.deliveredAt) : '—'}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-[10px] text-muted-foreground">
                        {r.suppressedReason
                          ? label(r.suppressedReason)
                          : r.failureReason ?? ''}
                      </span>
                    </Td>
                  </tr>
                ))}
              </ErpTable>
            </div>
          </Panel>
        </div>

        <div className="space-y-3">
          <Panel>
            <div className="space-y-1.5 p-3">
              <p className="text-[11px] font-medium">Who this reaches</p>
              <Reach recipients={campaign.recipientCount}
                     suppressed={campaign.suppressedCount} />
              <ul className="mt-1 space-y-1">
                {audiences.length === 0 && (
                  <li className="text-[10px] text-muted-foreground">
                    No audience yet.
                  </li>
                )}
                {audiences.map((a) => (
                  <li key={a.id} className="rounded border border-border p-1.5">
                    <span className="text-[11px]">{a.label}</span>
                    <span className="block text-[10px] text-muted-foreground">
                      {label(a.sourceCode)}
                      {a.estimatedSize != null
                        && ` · about ${a.estimatedSize.toLocaleString()} people`}
                    </span>
                  </li>
                ))}
              </ul>
              {canCompose && isDraft && (
                <button type="button" disabled={busy}
                        onClick={() => void run(() => materialise(id),
                          'Audience resolved. Every message is rendered and nothing is sent.')}
                        className="w-full rounded border border-border px-2 py-1 text-[11px]">
                  Resolve the audience
                </button>
              )}
            </div>
          </Panel>

          {canApprove && isDraft && (
            <Panel>
              <div className="space-y-1.5 p-3">
                <p className="text-[11px] font-medium">Approve this send</p>
                {campaign.recipientCount === 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    Resolve the audience first. Approving a send that reaches nobody spends a
                    decision on nothing.
                  </p>
                ) : (
                  <>
                    <dl className="space-y-0.5 text-[11px]">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">People</dt>
                        <dd className="tabular-nums">
                          {campaign.recipientCount.toLocaleString()}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Not contactable</dt>
                        <dd className="tabular-nums">
                          {campaign.suppressedCount.toLocaleString()}
                        </dd>
                      </div>
                      <div className="flex justify-between border-t border-border pt-0.5">
                        <dt className="text-muted-foreground">Estimated cost</dt>
                        <dd><Cost value={campaign.estimatedCostKes} /></dd>
                      </div>
                    </dl>
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">Send at (optional)</span>
                      <DateTimePicker
                        value={scheduledAt}
                        onChange={setScheduledAt}
                        className="h-7 rounded border border-input bg-background px-2 text-[12px]"
                      />
                      <span className="text-[10px] text-muted-foreground">
                        Leave empty to send as soon as the window is open.
                      </span>
                    </label>
                    <button type="button" disabled={busy}
                            onClick={() => void run(
                              () => approveCampaign(id, scheduledAt
                                ? new Date(scheduledAt).toISOString() : null),
                              'Approved. Your name is on this send.')}
                            className="inline-flex w-full items-center justify-center gap-1
                                       rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                                       text-primary-foreground disabled:opacity-50">
                      <Send className="size-3.5" /> Approve &amp; schedule
                    </button>
                    <p className="text-[10px] text-muted-foreground">
                      This is irreversible for everyone it reaches. Your user id goes on the
                      campaign and stays there.
                    </p>
                  </>
                )}
              </div>
            </Panel>
          )}

          <Panel>
            <div className="space-y-1.5 p-3">
              <p className="text-[11px] font-medium">Delivery</p>
              {accepted === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Nothing sent yet.
                </p>
              ) : (
                <>
                  <LabeledBar label="Confirmed delivered" value={delivered} max={accepted} />
                  <p className="text-[10px] text-muted-foreground">
                    <b>{accepted.toLocaleString()}</b> accepted by a provider ·{' '}
                    <b>{delivered.toLocaleString()}</b> confirmed delivered ·{' '}
                    <b className="text-rose-600">{failed.toLocaleString()}</b> failed
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    These are different claims. A provider accepting a message is not a handset
                    receiving one, and some providers never send a report at all.
                  </p>
                </>
              )}
              {Object.keys(breakdown).length > 0 && (
                <ul className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(breakdown).map(([code, count]) => (
                    <li key={code} className="inline-flex items-center gap-1">
                      <StatusChip code={code} />
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {count.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {campaign.actualCostKes && (
                <p className="border-t border-border pt-1 text-[11px]">
                  Spent: <Cost value={campaign.actualCostKes} />
                </p>
              )}
            </div>
          </Panel>

          {campaign.statusCode === 'SENT' && (
            <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <CheckCircle2 className="size-3.5 text-emerald-600" />
              Finished {fmtRelative(campaign.finishedAt)}.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
